import posthog from "posthog-js";
import type { CaptureResult, PostHogConfig, PostHogInterface } from "posthog-js";
import { BLOCKED_REPLAY_PATH_PREFIXES } from "./posthog-route-guard";
import { isSuppressedError } from "@/lib/error-suppression";
import { POSTHOG_HOST, POSTHOG_PROJECT_TOKEN } from "./posthog-config";

/** Derived from `BLOCKED_REPLAY_PATH_PREFIXES` — single source of truth for blocked routes. */
const SESSION_RECORDING_URL_BLOCKLIST: Array<{ url: string; matching: "regex" }> =
  BLOCKED_REPLAY_PATH_PREFIXES.map((prefix) => ({
    url: String.raw`.*` + prefix.replace(/\//g, "\\/") + String.raw`(\/.*)?(\?.*)?$`,
    matching: "regex",
  }));

const ANONYMOUS_SESSION_SAMPLE_RATE = 0.1;
const AUTHENTICATED_SESSION_SAMPLE_RATE = 1;

/**
 * Ingestion is reverse-proxied through our own origin (`/ingest`) by default so
 * ad-blockers (which blanket-block `*.posthog.com`) can't silently drop events —
 * otherwise a large slice of "where users come from / what they do" data never
 * arrives. The Next rewrites that back `/ingest` live in `next.config.mjs`; both
 * sides read `NEXT_PUBLIC_POSTHOG_PROXY` so they stay in lock-step.
 *
 * Set `NEXT_PUBLIC_POSTHOG_PROXY=0` to bypass the proxy and hit PostHog directly.
 */
const POSTHOG_INGEST_PROXY_PATH = "/ingest";

function isPosthogProxyEnabled(): boolean {
  return process.env.NEXT_PUBLIC_POSTHOG_PROXY !== "0";
}

let initStarted = false;

function devWarn(message: string) {
  if (process.env.NODE_ENV === "development") {
    console.warn(`[PostHog] ${message}`);
  }
}

/**
 * Attaches `sessionRecording.isCapturing()` for parity with docs/ascension-plan.md §5.5 tests
 * (SDK exposes `sessionRecordingStarted()` on the root client).
 */
function attachIsCapturingAlias(client: PostHogInterface) {
  const sr = client.sessionRecording as { isCapturing?: () => boolean } | undefined;
  if (!sr || typeof sr.isCapturing === "function") return;
  Object.defineProperty(sr, "isCapturing", {
    configurable: true,
    enumerable: false,
    value: () => client.sessionRecordingStarted(),
  });
}

/** Pull a human-readable message out of a PostHog `$exception` event for noise filtering. */
function exceptionMessageOf(event: CaptureResult): string {
  const props = (event.properties ?? {}) as Record<string, unknown>;
  if (typeof props.$exception_message === "string") return props.$exception_message;
  const list = props.$exception_list;
  if (Array.isArray(list)) {
    return list
      .map((e) => (e && typeof (e as { value?: unknown }).value === "string" ? (e as { value: string }).value : ""))
      .join(" ");
  }
  return "";
}

/**
 * The stack frames PostHog attached, flattened for origin checking.
 *
 * Native Error Tracking puts frames on each `$exception_list` entry rather than
 * in a `stack` string, so the filter has to reassemble them to see that an
 * error came from a browser extension.
 */
function exceptionStackOf(event: CaptureResult): string {
  const props = (event.properties ?? {}) as Record<string, unknown>;
  const parts: string[] = [];
  if (typeof props.$exception_stack_trace_raw === "string") {
    parts.push(props.$exception_stack_trace_raw);
  }
  const list = props.$exception_list;
  if (Array.isArray(list)) {
    for (const entry of list) {
      const frames = (entry as { stacktrace?: { frames?: unknown[] } })?.stacktrace?.frames;
      if (!Array.isArray(frames)) continue;
      for (const f of frames) {
        const filename = (f as { filename?: unknown })?.filename;
        if (typeof filename === "string") parts.push(filename);
      }
    }
  }
  return parts.join("\n");
}

/**
 * Drop `$exception` events that match the shared suppression list (browser
 * extension noise, hydration warnings, framework nav throws) so PostHog's native
 * Error Tracking Issues stay signal-rich. All other events pass through untouched.
 *
 * Checks the stack as well as the message: one Chrome extension generated 32
 * reports reading `Cannot read properties of undefined (reading 'M_ID')`, a
 * message no pattern list should ever suppress on its own — it is exactly what
 * our own null bugs look like.
 */
function filterSuppressedExceptions(event: CaptureResult | null): CaptureResult | null {
  if (
    event &&
    event.event === "$exception" &&
    isSuppressedError(exceptionMessageOf(event), exceptionStackOf(event))
  ) {
    return null;
  }
  return event;
}

function buildInitConfig(): Partial<PostHogConfig> {
  const host = POSTHOG_HOST;
  const proxied = isPosthogProxyEnabled();

  return {
    // First-party proxy by default (`/ingest`) so ad-blockers can't drop events;
    // `ui_host` keeps "View in PostHog" deep links pointing at the real app.
    api_host: proxied ? POSTHOG_INGEST_PROXY_PATH : host,
    ui_host: host,
    capture_pageview: false,
    persistence: "localStorage+cookie",
    opt_out_capturing_by_default: true,
    autocapture: true,
    /**
     * Only create a PostHog *person* once a user identifies (sign-in). Anonymous
     * traffic still streams events carrying GeoIP, referrer and UTM params — so
     * "where anonymous visitors come from" is fully visible — but we don't pay to
     * persist a profile per bot/one-off visitor. Logged-in users get a full,
     * enriched person (see `identity.ts`).
     */
    person_profiles: "identified_only",
    /**
     * Native Error Tracking: auto-capture uncaught exceptions + unhandled
     * promise rejections as `$exception` events (grouped into Issues in PostHog),
     * with full stack traces from the production browser source maps shipped by
     * `next.config.mjs`. Caught/curated errors keep flowing through
     * `reportErrorToDiscord` for the source/severity-tagged feed.
     */
    capture_exceptions: true,
    /** Keep native Error Tracking signal-rich — drop known extension/hydration noise. */
    before_send: filterSuppressedExceptions,
    /** Core Web Vitals (LCP, CLS, INP, FCP) as `$web_vitals` — "what users actually experience". */
    capture_performance: { web_vitals: true },
    /** 15 min session idle — pairs with PostHog replay retention; replay max length is also set in PostHog project settings. */
    session_idle_timeout_seconds: 15 * 60,
    session_recording: {
      sampleRate: ANONYMOUS_SESSION_SAMPLE_RATE,
      maskAllInputs: true,
      maskInputOptions: {
        color: true,
        date: true,
        "datetime-local": true,
        email: true,
        month: true,
        number: true,
        range: true,
        search: false,
        tel: true,
        text: true,
        time: true,
        url: true,
        week: true,
        textarea: true,
        select: true,
        password: true,
      },
      urlBlocklist: SESSION_RECORDING_URL_BLOCKLIST,
    } as PostHogConfig["session_recording"],
    loaded: (client) => {
      if (typeof window !== "undefined") {
        (window as unknown as { posthog: typeof client }).posthog = client;
      }
      attachIsCapturingAlias(client);
    },
    // Prevent the dead-clicks-autocapture lazy script from loading (412 ms TTFB on cold visit)
    capture_dead_clicks: false,
  };
}

/**
 * Lazy init — safe to call multiple times. No-ops when token/host missing.
 */
export function getPosthog() {
  if (typeof window === "undefined") {
    return posthog;
  }

  if (!POSTHOG_PROJECT_TOKEN) {
    devWarn("NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN missing; PostHog disabled.");
    return posthog;
  }

  if (!initStarted) {
    initStarted = true;
    posthog.init(POSTHOG_PROJECT_TOKEN, buildInitConfig() as PostHogConfig);
  }

  return posthog;
}

export function applySessionRecordingSampleForAuth(isAuthenticated: boolean) {
  const client = getPosthog();
  if (!POSTHOG_PROJECT_TOKEN) return;

  client.set_config({
    session_recording: {
      sampleRate: isAuthenticated ? AUTHENTICATED_SESSION_SAMPLE_RATE : ANONYMOUS_SESSION_SAMPLE_RATE,
    },
  });
}
